import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import SiteJobsTab from './SiteJobsTab';
import SiteEquipmentTab from './SiteEquipmentTab';
import SiteContractsTab from './SiteContractsTab';
import SiteQuotesTab from './SiteQuotesTab';
import SiteInvoicesTab from './SiteInvoicesTab';
import SitePurchaseOrdersTab from './SitePurchaseOrdersTab';
import SiteSalesOrdersTab from './SiteSalesOrdersTab';
import AddJobModal from './AddJobModal';
import { apiFetch } from './utils/api';

function SiteDetailModern() {
  const { siteId, customerId, id } = useParams();
  const navigate = useNavigate();
  const actualSiteId = siteId || id;
  
  // Debug logging
  console.log('SiteDetail params:', { siteId, customerId, id, actualSiteId });
  
  const handleBack = () => {
    console.log('handleBack called, customerId:', customerId);
    if (customerId) {
      console.log('Navigating to /customers/' + customerId);
      navigate(`/customers/${customerId}`);
    } else {
      console.log('Navigating to /sites');
      navigate("/sites");
    }
  };
  const [site, setSite] = useState(null);
  const [editedSite, setEditedSite] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [equipment, setEquipment] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');
  const [showAddJobModal, setShowAddJobModal] = useState(false);
  const [showAddContactModal, setShowAddContactModal] = useState(false);
  const [siteTypes, setSiteTypes] = useState([]);

  useEffect(() => {
    fetchSite();
    fetchEquipment();
    fetchContacts();
    fetchSiteTypes();
  }, [siteId]);

  const fetchSiteTypes = async () => {
    try {
      const res = await apiFetch('/api/custom-lists?list_type=site_type');
      const data = await res.json();
      setSiteTypes(data);
    } catch (err) {
      console.error('Error fetching site types:', err);
    }
  };

  const fetchSite = async () => {
    try {
      const res = await apiFetch(`/api/sites/${actualSiteId}`);
      const data = await res.json();
      setSite(data);
      setEditedSite(data);
      setLoading(false);
      
      // Fetch customer after we have site data
      if (data.customer_id) {
        fetchCustomer(data.customer_id);
      }
    } catch (err) {
      console.error('Error fetching site:', err);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await apiFetch(`/api/sites/${actualSiteId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(editedSite)
      });
      
      if (res.ok) {
        const updated = await res.json();
        setSite(updated);
        setEditedSite(updated);
        alert('Site updated successfully!');
      } else {
        alert('Failed to update site');
      }
    } catch (err) {
      console.error('Error saving site:', err);
      alert('Error saving site');
    }
    setSaving(false);
  };

  const fetchCustomer = async (custId) => {
    try {
      const res = await apiFetch(`/api/customers/${custId}`);
      const data = await res.json();
      setCustomer(data);
    } catch (err) {
      console.error('Error fetching customer:', err);
    }
  };

  const fetchEquipment = async () => {
    try {
      const res = await apiFetch(`/api/doors?site_id=${actualSiteId}`);
      if (res.ok) {
        const data = await res.json();
        setEquipment(Array.isArray(data) ? data : []);
      } else {
        // Doors table not implemented yet
        setEquipment([]);
      }
    } catch (err) {
      console.error('Error fetching equipment:', err);
      setEquipment([]);
    }
  };

  const fetchContacts = async () => {
    try {
      const res = await apiFetch(`/api/site-contacts/site/${actualSiteId}`);
      const data = await res.json();
      setContacts(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Error fetching site contacts:', err);
      setContacts([]);
    }
  };

  const handleAddContact = async (contactData) => {
    try {
      const res = await apiFetch('/api/site-contacts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...contactData, site_id: siteId })
      });
      
      if (res.ok) {
        fetchContacts();
        setShowAddContactModal(false);
      } else {
        alert('Failed to add contact');
      }
    } catch (err) {
      console.error('Error adding contact:', err);
      alert('Error adding contact');
    }
  };

  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    
    try {
      const res = await apiFetch(`/api/site-contacts/${contactId}`, {
        method: 'DELETE'
      });
      
      if (res.ok) {
        fetchContacts();
      } else {
        alert('Failed to delete contact');
      }
    } catch (err) {
      console.error('Error deleting contact:', err);
      alert('Error deleting contact');
    }
  };

  if (loading || !site || !editedSite) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', count: null },
    { id: 'contacts', label: 'Contacts', count: contacts.length },
    { id: 'equipment', label: 'Equipment', count: equipment.length },
    { id: 'contracts', label: 'PPM Contracts', icon: '📋' },
    { id: 'jobs', label: 'Jobs', count: 0 },
    { id: 'quotes', label: 'Quotes', icon: '📄' },
    { id: 'invoices', label: 'Invoices', icon: '💳' },
    { id: 'purchase-orders', label: 'Purchase Orders', icon: '📦' },
    { id: 'sales-orders', label: 'Sales Orders', icon: '🛒' },
    { id: 'finance', label: 'Financial', icon: '💰' },
    { id: 'notes', label: 'Notes', count: null },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={handleBack}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">{site.site_name}</h1>
              <span className="text-sm text-blue-100">{editedSite.site_type === 'head_office' ? 'Head Office' : 'Site'}</span>
            </div>
            <div className="flex items-center space-x-2">
              <button 
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
              >
                {saving ? '⏳ Saving...' : '💾 Save'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-full px-4 py-3">
        <div className="bg-gray-50 rounded-xl shadow-sm p-2 border border-gray-200 inline-flex space-x-1">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === tab.id
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-900'
              }`}
            >
              {tab.label}
              {tab.count !== null && (
                <span className={`ml-2 px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id ? 'bg-gray-700' : 'bg-gray-200'
                }`}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 pb-3">
        <div className="rounded-xl bg-gray-100 border-4 border-gray-400 shadow-[inset_6px_6px_12px_rgba(0,0,0,0.3),inset_-4px_-4px_8px_rgba(255,255,255,0.9)] overflow-auto max-h-[calc(100vh-135px)] min-h-[650px]">
          
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Site Information</h2>
                <div className="flex items-center space-x-2">
                  <button 
                    onClick={() => setShowAddJobModal(true)}
                    className="px-3 py-1.5 bg-gray-300 hover:bg-gray-400 rounded text-black text-sm font-medium transition"
                  >
                    ➕ Add a Job
                  </button>
                  <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-lg text-sm font-semibold">
                    📍 {editedSite.site_type === 'head_office' ? 'Head Office' : 'Site'}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Left Column */}
                <div className="space-y-5">
                  <div>
                    <label className="field-label mb-2 block">Site Name</label>
                    <input
                      type="text"
                      value={editedSite?.site_name || ''}
                      onChange={(e) => setEditedSite({...editedSite, site_name: e.target.value})}
                      className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value text-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  
                  <div>
                    <label className="field-label mb-2 block">Site Contact</label>
                    <input
                      type="text"
                      value={editedSite?.contact_name || ''}
                      onChange={(e) => setEditedSite({...editedSite, contact_name: e.target.value})}
                      className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      placeholder="Contact name"
                    />
                  </div>

                  <div>
                    <label className="field-label mb-1.5 block">Site Type</label>
                    <select 
                      className="px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      value={editedSite?.site_type || 'site'}
                      onChange={(e) => setEditedSite({...editedSite, site_type: e.target.value})}
                    >
                      {siteTypes.map(type => (
                        <option key={type.id} value={type.list_value}>
                          {type.list_value.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Right Column */}
                <div className="space-y-5">
                  <div>
                    <label className="field-label mb-2 block">Address</label>
                    <div className="space-y-2">
                      <input
                        type="text"
                        value={editedSite?.address || ''}
                        onChange={(e) => setEditedSite({...editedSite, address: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Street address"
                      />
                      <input
                        type="text"
                        value={editedSite?.city || ''}
                        onChange={(e) => setEditedSite({...editedSite, city: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="City"
                      />
                      <input
                        type="text"
                        value={editedSite?.county || ''}
                        onChange={(e) => setEditedSite({...editedSite, county: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="County"
                      />
                      <input
                        type="text"
                        value={editedSite?.postcode || ''}
                        onChange={(e) => setEditedSite({...editedSite, postcode: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value font-semibold focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="Postcode"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="field-label mb-2 block">Contact Details</label>
                    <div className="space-y-2">
                      <input
                        type="tel"
                        value={editedSite?.phone || ''}
                        onChange={(e) => setEditedSite({...editedSite, phone: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="📞 Phone"
                      />
                      <input
                        type="tel"
                        value={editedSite?.mobile || ''}
                        onChange={(e) => setEditedSite({...editedSite, mobile: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="📱 Mobile"
                      />
                      <input
                        type="email"
                        value={editedSite?.email || ''}
                        onChange={(e) => setEditedSite({...editedSite, email: e.target.value})}
                        className="w-full bg-gray-50 border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded px-3 py-2 data-value focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="📧 Email"
                      />
                    </div>
                  </div>
                </div>
              </div>


            </div>
          )}

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div className="p-8">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-semibold text-gray-900">Site Contacts</h2>
                <button 
                  onClick={() => setShowAddContactModal(true)}
                  className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium shadow-sm transition"
                >
                  ➕ Add Contact
                </button>
              </div>

              {contacts.length === 0 ? (
                <div className="text-center py-12">
                  <div className="text-6xl mb-4">👥</div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">No contacts yet</h3>
                  <p className="text-gray-500 mb-4">Add contacts specific to this site</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {contacts.map((contact) => (
                    <div key={contact.id} className="bg-white border-2 border-gray-300 shadow-[inset_2px_2px_4px_rgba(0,0,0,0.15)] rounded-xl p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className="font-semibold text-gray-900 text-lg">{contact.name}</h3>
                            {contact.is_primary && (
                              <span className="px-2 py-0.5 bg-blue-100 text-blue-700 rounded text-xs font-semibold">
                                PRIMARY
                              </span>
                            )}
                          </div>
                          {contact.role && (
                            <p className="text-sm text-gray-600">{contact.role}</p>
                          )}
                        </div>
                        <button
                          onClick={() => handleDeleteContact(contact.id)}
                          className="text-red-600 hover:text-red-800 text-sm"
                        >
                          🗑️
                        </button>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        {contact.phone && (
                          <div className="flex items-center space-x-2 text-gray-700">
                            <span className="text-green-600">📞</span>
                            <span>{contact.phone}</span>
                          </div>
                        )}
                        {contact.mobile && (
                          <div className="flex items-center space-x-2 text-gray-700">
                            <span className="text-green-600">📱</span>
                            <span>{contact.mobile}</span>
                          </div>
                        )}
                        {contact.email && (
                          <div className="flex items-center space-x-2 text-gray-700">
                            <span className="text-green-600">📧</span>
                            <span>{contact.email}</span>
                          </div>
                        )}
                      </div>
                      
                      {contact.notes && (
                        <div className="mt-3 pt-3 border-t border-gray-200">
                          <p className="text-sm text-gray-600 italic">{contact.notes}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Equipment Tab */}
          {activeTab === 'equipment' && (
            <SiteEquipmentTab 
              siteId={parseInt(actualSiteId)}
              siteName={site.site_name}
            />
          )}

          {/* PPM Contracts Tab */}
          {activeTab === 'contracts' && (
            <SiteContractsTab 
              siteId={parseInt(actualSiteId)}
              siteName={site.site_name}
            />
          )}

          {/* Jobs Tab */}
          {activeTab === 'jobs' && (
            <SiteJobsTab 
              siteId={parseInt(actualSiteId)} 
              siteName={site.site_name}
              customerId={site.customer_id}
              customerName={customer?.company_name || 'Customer'}
            />
          )}

          {/* Quotes Tab */}
          {activeTab === 'quotes' && (
            <SiteQuotesTab 
              siteId={parseInt(actualSiteId)} 
              siteName={site.site_name}
              customerId={site.customer_id}
              customerName={customer?.company_name || 'Customer'}
            />
          )}

          {/* Invoices Tab */}
          {activeTab === 'invoices' && (
            <SiteInvoicesTab 
              siteId={parseInt(actualSiteId)} 
              siteName={site.site_name}
              customerId={site.customer_id}
              customerName={customer?.company_name || 'Customer'}
            />
          )}

          {/* Purchase Orders Tab */}
          {activeTab === 'purchase-orders' && (
            <SitePurchaseOrdersTab 
              siteId={parseInt(actualSiteId)} 
              siteName={site.site_name}
              customerId={site.customer_id}
              customerName={customer?.company_name || 'Customer'}
            />
          )}

          {/* Sales Orders Tab */}
          {activeTab === 'sales-orders' && (
            <SiteSalesOrdersTab 
              siteId={parseInt(actualSiteId)} 
              siteName={site.site_name}
              customerId={site.customer_id}
              customerName={customer?.company_name || 'Customer'}
            />
          )}

          {activeTab === 'finance' && (
            <div className="p-8 text-center">
              <div className="text-6xl mb-4">💰</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Financial</h3>
              <p className="text-gray-500">Coming soon</p>
            </div>
          )}

          {activeTab === 'notes' && (
            <div className="p-8 text-center">
              <div className="text-6xl mb-4">📝</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Notes</h3>
              <p className="text-gray-500">Notes feature coming soon</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Contact Modal */}
      {showAddContactModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Add Site Contact</h3>
            <form onSubmit={(e) => {
              e.preventDefault();
              const formData = new FormData(e.target);
              handleAddContact({
                name: formData.get('name'),
                role: formData.get('role'),
                phone: formData.get('phone'),
                mobile: formData.get('mobile'),
                email: formData.get('email'),
                is_primary: formData.get('is_primary') === 'on',
                notes: formData.get('notes')
              });
            }}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                  <input
                    type="text"
                    name="name"
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Contact name"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <input
                    type="text"
                    name="role"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Site Manager, Security, etc."
                  />
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                    <input
                      type="tel"
                      name="phone"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                    <input
                      type="tel"
                      name="mobile"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    name="email"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    name="notes"
                    rows="2"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Additional notes..."
                  />
                </div>

                <div className="flex items-center">
                  <input
                    type="checkbox"
                    name="is_primary"
                    id="is_primary"
                    className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="is_primary" className="ml-2 text-sm text-gray-700">
                    Set as primary contact for this site
                  </label>
                </div>
              </div>

              <div className="flex space-x-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowAddContactModal(false)}
                  className="flex-1 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition"
                >
                  Add Contact
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add Job Modal */}
      {showAddJobModal && site && customer && (
        <AddJobModal
          customerId={site.customer_id}
          customerName={customer.company_name}
          siteId={site.id}
          siteName={site.site_name}
          onClose={() => setShowAddJobModal(false)}
          onJobCreated={() => setShowAddJobModal(false)}
        />
      )}
    </div>
  );
}

export default SiteDetailModern;
